/**
 * ClassificationRuleCard Component
 *
 * Card component for displaying a classification rule.
 * Shows name, pattern, target account, priority, match stats, and action buttons.
 *
 * Features:
 * - Rule name as header
 * - Pattern display in monospace style
 * - Target account path
 * - Priority badge
 * - Match statistics (count and last match date)
 * - Active/Inactive toggle switch
 * - Edit and Delete action buttons
 * - Muted styling for inactive rules
 * - Loading state during actions
 */

import { useState } from 'react';
import clsx from 'clsx';
import {
  Pencil,
  Trash2,
  Hash,
  Calendar,
  Target,
  Loader2,
  ToggleLeft,
  ToggleRight,
  ArrowUp,
  Eye,
  CheckSquare,
  Square,
} from 'lucide-react';
import type { ClassificationRule } from '@/types/accounting';

export interface ClassificationRuleCardProps {
  /** The classification rule to display */
  rule: ClassificationRule;
  /** Callback when the edit button is clicked */
  onEdit: (rule: ClassificationRule) => void;
  /** Callback when the delete button is clicked */
  onDelete: (id: number) => Promise<void>;
  /** Callback when active status is toggled */
  onToggleActive: (id: number, active: boolean) => Promise<void>;
  /** Callback when the preview button is clicked */
  onPreview: (rule: ClassificationRule) => void;
  /** Whether actions are disabled */
  disabled?: boolean;
  /** Whether this rule is selected */
  selected?: boolean;
  /** Callback when selection is toggled */
  onToggleSelect?: (id: number) => void;
  /** Whether to show selection checkbox (selection mode) */
  selectionMode?: boolean;
}

/**
 * Format a date string for display
 */
function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format a relative time string
 */
function formatRelativeTime(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never matched';

  const date = new Date(dateStr);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  return formatDate(dateStr);
}

/**
 * ClassificationRuleCard displays a rule with edit, delete, and toggle actions.
 *
 * @example
 * <ClassificationRuleCard
 *   rule={classificationRule}
 *   onEdit={handleEdit}
 *   onDelete={handleDelete}
 *   onToggleActive={handleToggleActive}
 * />
 */
export function ClassificationRuleCard({
  rule,
  onEdit,
  onDelete,
  onToggleActive,
  onPreview,
  disabled = false,
  selected = false,
  onToggleSelect,
  selectionMode = false,
}: ClassificationRuleCardProps) {
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const isLoading = isToggling || isDeleting;
  const isDisabled = disabled || isLoading;

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleSelect) {
      onToggleSelect(rule.id);
    }
  };

  const handleToggle = async () => {
    setIsToggling(true);
    try {
      await onToggleActive(rule.id, !rule.active);
    } finally {
      setIsToggling(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(rule.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = () => {
    onEdit(rule);
  };

  return (
    <div
      className={clsx(
        'bg-white rounded-lg border overflow-hidden',
        'transition-all hover:shadow-md',
        rule.active ? 'border-gray-200' : 'border-gray-200 bg-gray-50',
        isLoading && 'opacity-70',
        !rule.active && 'opacity-60'
      )}
      data-testid="classification-rule-card"
      data-rule-id={rule.id}
    >
      {/* Header with name and toggle */}
      <div
        className={clsx(
          'px-4 py-3 border-b flex items-center justify-between',
          rule.active ? 'bg-gray-50 border-gray-200' : 'bg-gray-100 border-gray-200'
        )}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Selection Checkbox */}
          {selectionMode && (
            <button
              type="button"
              onClick={handleSelectClick}
              className={clsx(
                'flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded',
                isDisabled && 'cursor-not-allowed opacity-50'
              )}
              disabled={isDisabled}
              data-testid="rule-select-checkbox"
              aria-label={selected ? 'Deselect rule' : 'Select rule'}
            >
              {selected ? (
                <CheckSquare size={18} className="text-blue-600" />
              ) : (
                <Square size={18} className="text-gray-400 hover:text-gray-600" />
              )}
            </button>
          )}
          <h3
            className={clsx(
              'font-medium truncate',
              rule.active ? 'text-gray-900' : 'text-gray-500'
            )}
            title={rule.name}
            data-testid="rule-name"
          >
            {rule.name}
          </h3>
          {rule.priority > 0 && (
            <span
              className={clsx(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium',
                rule.active
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-200 text-gray-500'
              )}
              title={`Priority: ${rule.priority}`}
            >
              <ArrowUp size={12} />
              {rule.priority}
            </span>
          )}
        </div>

        {/* Active Toggle */}
        <button
          type="button"
          onClick={handleToggle}
          disabled={isDisabled}
          className={clsx(
            'flex items-center gap-2 text-sm transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded',
            rule.active
              ? 'text-green-600 hover:text-green-700'
              : 'text-gray-400 hover:text-gray-500',
            isDisabled && 'cursor-not-allowed'
          )}
          data-testid="toggle-active-button"
          title={rule.active ? 'Click to disable rule' : 'Click to enable rule'}
        >
          {isToggling ? (
            <Loader2 size={20} className="animate-spin" />
          ) : rule.active ? (
            <ToggleRight size={24} className="text-green-600" />
          ) : (
            <ToggleLeft size={24} className="text-gray-400" />
          )}
          <span className="sr-only">
            {rule.active ? 'Active' : 'Inactive'}
          </span>
        </button>
      </div>

      {/* Pattern */}
      <div className="px-4 py-3 border-b border-gray-100">
        <code
          className={clsx(
            'block px-3 py-2 rounded text-sm font-mono break-all',
            rule.active
              ? 'bg-gray-900 text-green-400'
              : 'bg-gray-700 text-gray-400'
          )}
          data-testid="rule-pattern"
        >
          /{rule.pattern}/i
        </code>
      </div>

      {/* Target Account */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 text-sm">
          <Target
            size={16}
            className={rule.active ? 'text-gray-400' : 'text-gray-300'}
          />
          <span className={rule.active ? 'text-gray-500' : 'text-gray-400'}>
            Target:
          </span>
          <span
            className={clsx(
              'font-medium',
              rule.active ? 'text-gray-900' : 'text-gray-500'
            )}
            data-testid="target-account"
          >
            {rule.targetAccountPath}
          </span>
        </div>
      </div>

      {/* Match Stats */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm">
          <div
            className="flex items-center gap-1.5"
            title={`${rule.matchCount} total matches`}
          >
            <Hash
              size={14}
              className={rule.active ? 'text-blue-500' : 'text-gray-400'}
            />
            <span
              className={clsx(
                'font-medium',
                rule.active ? 'text-gray-700' : 'text-gray-500'
              )}
              data-testid="match-count"
            >
              {rule.matchCount}
            </span>
            <span className={rule.active ? 'text-gray-500' : 'text-gray-400'}>
              matches
            </span>
          </div>
          <div
            className="flex items-center gap-1.5"
            title={rule.lastMatchedAt ? `Last matched: ${formatDate(rule.lastMatchedAt)}` : 'Never matched'}
          >
            <Calendar
              size={14}
              className={rule.active ? 'text-gray-400' : 'text-gray-300'}
            />
            <span
              className={rule.active ? 'text-gray-500' : 'text-gray-400'}
              data-testid="last-matched"
            >
              {formatRelativeTime(rule.lastMatchedAt)}
            </span>
          </div>
        </div>

        {/* Owner Default Badge */}
        <span
          className={clsx(
            'px-2 py-0.5 rounded text-xs font-medium',
            rule.active
              ? rule.ownerDefault === 'family'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-amber-100 text-amber-700'
              : 'bg-gray-200 text-gray-500'
          )}
          data-testid="owner-default"
        >
          {rule.ownerDefault}
        </span>
      </div>

      {/* Actions */}
      <div className="px-4 py-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => onPreview(rule)}
          disabled={isDisabled}
          className={clsx(
            'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md',
            'bg-white text-blue-600 border border-blue-300 hover:bg-blue-50',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          data-testid="preview-button"
        >
          <Eye size={16} />
          Preview
        </button>
        <button
          type="button"
          onClick={handleEdit}
          disabled={isDisabled}
          className={clsx(
            'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md',
            'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50',
            'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          data-testid="edit-button"
        >
          <Pencil size={16} />
          Edit
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={isDisabled}
          className={clsx(
            'inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md',
            'bg-white text-red-600 border border-red-300 hover:bg-red-50',
            'focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
          data-testid="delete-button"
        >
          {isDeleting ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Trash2 size={16} />
          )}
          {isDeleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  );
}

export default ClassificationRuleCard;
